/**
 * Spacing tokens.
 *
 * Two complementary scales:
 *
 *  1. `space[0..10]` — the rigid metric scale (4 / 8 / 12 / 16 / 24 / 32 /
 *     48 / 64 / 96 / 128). This is the rhythm units of every component
 *     (paddings, gaps, fixed gutters). It is intentionally fixed-pixel so
 *     that adjacent elements share a stable horizon regardless of the type
 *     scale's fluid clamp() behavior. The scale follows roughly a 1.5×
 *     progression past 16, which gives clearly-distinct steps for hierarchy
 *     without going geometric (atlas 02 §1, "modular scale").
 *
 *  2. `fluidSpace[xs..3xl]` — viewport-fluid spacing for *page-level*
 *     gutters and section rhythm. These are the "atmospheric" intervals —
 *     the page margins, the gap between major regions, the breathing room
 *     around heroes. Built with `clamp()` so a 360px phone and a 1440px
 *     desktop both feel correctly aerated (atlas 03 §3, "Utopia space").
 *
 * `space` keeps its existing two-level shape (numeric keys → string values)
 * so the cssVars.ts flattener and every existing consumer continues to
 * work without modification. `fluidSpace` is additive.
 */

export const space = {
    0: "0",
    1: "4px",
    2: "8px",
    3: "12px",
    4: "16px",
    5: "24px",
    6: "32px",
    7: "48px",
    8: "64px",
    9: "96px",
    10: "128px",
} as const;

// Same `fluid()` formula as typography.ts — clamp between viewport 360..1280.
function fluid(minPx: number, maxPx: number): string {
    const minVw = 360;
    const maxVw = 1280;
    const slope = ((maxPx - minPx) * 100) / (maxVw - minVw);
    const intercept = minPx - (slope * minVw) / 100;
    const preferred = `calc(${intercept.toFixed(3)}px + ${slope.toFixed(3)}vw)`;
    return `clamp(${String(minPx)}px, ${preferred}, ${String(maxPx)}px)`;
}

/**
 * Page-level fluid spacing. Use for view padding, hero margins, the gap
 * between sibling compositions on the home view — anywhere the *page*
 * needs to breathe in proportion to the viewport.
 *
 * Component-internal spacing should still use `space[n]` so that paddings,
 * borders, and inter-element gaps remain on a shared metric grid.
 */
export const fluidSpace = {
    xs: fluid(8, 12), // dense gap, e.g. inside a chip
    sm: fluid(12, 16), // small section gap
    md: fluid(16, 24), // standard section gap
    lg: fluid(24, 40), // page padding (mobile→desktop)
    xl: fluid(32, 64), // hero padding, between major regions
    "2xl": fluid(48, 96), // generous hero, vertical air
    "3xl": fluid(64, 128), // landing-page-class breathing room
} as const;

export type SpaceTokens = typeof space;
export type SpaceKey = keyof SpaceTokens;
export type FluidSpaceKey = keyof typeof fluidSpace;
